import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import { CreateRequisitionRequest } from '../../models/requisitions/interfaces/create-requisition-request';
import { ExternalRequisition, ExternalRequisitionManagerChanges } from '../../models/requisitions/interfaces/external-requisition';
import { ExternalRequisitionSearch } from '../../models/requisitions/interfaces/external-requisition-search';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class RequisitionProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/external-requisition';

  public createFromSupplyRequest(request: CreateRequisitionRequest): Observable<ApiResponse<ExternalRequisition>> {
    return this.httpClient.post<ApiResponse<ExternalRequisition>>(`${this.baseUrl}/create`, request);
  }

  public getAllRequisitions(): Observable<ApiResponse<ExternalRequisition[]>> {
    return this.httpClient.get<ApiResponse<ExternalRequisition[]>>(this.baseUrl);
  }

  public getRequisitionById(id: string): Observable<ApiResponse<ExternalRequisition>> {
    return this.httpClient.get<ApiResponse<ExternalRequisition>>(`${this.baseUrl}/${id}`);
  }

  public upsertRequisition(data: ExternalRequisition): Observable<ApiResponse<ExternalRequisition>> {
    return this.httpClient.post<ApiResponse<ExternalRequisition>>(this.baseUrl, data);
  }

  public deleteRequisition(id: string): Observable<ApiResponse<boolean>> {
    return this.httpClient.delete<ApiResponse<boolean>>(`${this.baseUrl}/${id}`);
  }

  public updateRequisitionStatus(id: string, statusCode: number): Observable<ApiResponse<ExternalRequisition>> {
    return this.httpClient.post<ApiResponse<ExternalRequisition>>(`${this.baseUrl}/${id}/status/${statusCode}`, {});
  }

  public approveAndSplitRequisition(id: string): Observable<ApiResponse<ExternalRequisition[]>> {
    return this.httpClient.post<ApiResponse<ExternalRequisition[]>>(`${this.baseUrl}/${id}/approve`, {});
  }

  public searchRequisition(
    searchCriteria: ExternalRequisitionSearch,
    needUserApproval = false,
    excludeParentRequisitions = false,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
  ): Observable<ApiResponse<PaginatedApiResponse<ExternalRequisition>>> {
    let params = new HttpParams()
      .set('needUserApproval', needUserApproval.toString())
      .set('excludeParentRequisitions', excludeParentRequisitions.toString())
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder !== undefined) params = params.set('sortOrder', sortOrder.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ExternalRequisition>>>(`${this.baseUrl}/search`, searchCriteria, {
      params,
    });
  }

  public searchPaginatedRequisitions(
    searchCriteria: ExternalRequisitionSearch,
    needUserApproval = false,
    excludeParentRequisitions = false,
    pageNumber = 1,
    pageSize = 25,
    groupByParent = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ExternalRequisition>>> {
    const params = new HttpParams()
      .set('needUserApproval', needUserApproval.toString())
      .set('excludeParentRequisitions', excludeParentRequisitions.toString())
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('groupByParent', groupByParent.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ExternalRequisition>>>(
      `${this.baseUrl}/search/paginated`,
      searchCriteria,
      { params },
    );
  }

  public saveManagerChanges(requisition: ExternalRequisition, hardApprove: boolean): Observable<ApiResponse<ExternalRequisition[]>> {
    const command: ExternalRequisitionManagerChanges = {
      requisition: requisition,
      needsCreatorFeedback: !hardApprove,
    };
    return this.httpClient.post<ApiResponse<ExternalRequisition[]>>(`${this.baseUrl}/${requisition.id}/manager-changes`, command);
  }

  public downloadRequisitionPdf(requisitionId: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/${requisitionId}/report`, { responseType: 'blob' });
  }
}
